/**
 * Тонкая обёртка над fetch.
 * Vite dev server проксирует /api → http://localhost:8001 (см. vite.config.ts),
 * поэтому baseUrl не нужен в dev. В проде nginx (Фаза 3) делает то же самое.
 *
 * Если запускаешь фронт отдельно от gateway (cross-origin) — выставь VITE_GATEWAY_URL.
 */

const baseUrl = import.meta.env.VITE_GATEWAY_URL ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type ApiOptions = RequestInit & {
  // Не уведомлять обработчик истёкшей сессии при 401/403 на этом запросе.
  // Нужно для самого auth-флоу (login/register) и для терпимых пробников.
  skipAuthHandler?: boolean;
};

let sessionExpiredHandler: (() => void) | null = null;

/**
 * Регистрирует колбэк, который вызывается, когда любой API-запрос вернул
 * 401/403 — то есть сессия на бэке больше не валидна. Передай null, чтобы снять.
 */
export function setSessionExpiredHandler(fn: (() => void) | null) {
  sessionExpiredHandler = fn;
}

export async function api<T>(path: string, init?: ApiOptions): Promise<T> {
  const { skipAuthHandler, ...fetchInit } = init ?? {};
  const res = await fetch(baseUrl + path, {
    credentials: 'include',
    ...fetchInit,
    headers: {
      'Content-Type': 'application/json',
      ...(fetchInit.headers ?? {}),
    },
  });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    if ((res.status === 401 || res.status === 403) && !skipAuthHandler) {
      sessionExpiredHandler?.();
    }
    throw new ApiError(`API ${res.status} on ${path}`, res.status, body);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}
